import AccountSettings from './ui/AccountSettings'
import { useSearchParams } from 'react-router-dom'
import type { ComponentType } from 'react'
import { Settings as SettingsIcon, Mail, Printer, Truck, Package, ShoppingBag, Trash2, Sparkles, Store, CalendarClock } from 'lucide-react'
import { cn } from '../lib/shadcn/utils'
import PrintLabelSettings from './ui/PrintLabelSettings'
import ShippingSettings from './ui/ShippingSettings'
import TrackingEmailSettings from './ui/TrackingEmailSettings'
import EmailVoiceSettings from './ui/EmailVoiceSettings'
import EmailTemplatesSettings from './ui/EmailTemplatesSettings'
import EmailSignatureSettings from './ui/EmailSignatureSettings'
import MailboxConnectionSettings from './ui/MailboxConnectionSettings'
import AvailableSkusSettings from './ui/AvailableSkusSettings'
import ProductOptionsSettings from './ui/ProductOptionsSettings'
import WholesaleTierMinimumsSettings from './ui/WholesaleTierMinimumsSettings'
import TierReviewSettingsCard from './ui/TierReviewSettings'
import UnitsSettings from './ui/UnitsSettings'
import FixedCostsSettings from './ui/FixedCostsSettings'
import ProductDefaultsSettings from './ui/ProductDefaultsSettings'
import PhotoCleanupSettings from './ui/PhotoCleanupSettings'
import FaireSettings from './ui/FaireSettings'
import DescriptionAiSettings from './ui/DescriptionAiSettings'
import BagReorderSettings from './ui/BagReorderSettings'
import BagDimensionsSettings from './ui/BagDimensionsSettings'
import BagsPanel from './ui/BagsPanel'
import WeeklyDigestSettings from './ui/WeeklyDigestSettings'
import RecentlyDeletedSettings from './ui/RecentlyDeletedSettings'
import ScheduledJobsSettings from './ui/ScheduledJobsSettings'

type SettingsItem = { value: string; label: string; component: ComponentType }
type SettingsSection = {
  value: string
  label: string
  icon: ComponentType<{ className?: string }>
  items: SettingsItem[]
}

// Every settings card has its own entry, so the page shows one card at a time
// instead of one long scroll. Links use ?section=<value> and optionally
// &item=<value>; a section on its own opens its first card.
const SECTIONS: SettingsSection[] = [
  { value: 'account', label: 'Account', icon: SettingsIcon, items: [{ value: 'profile', label: 'Profile & users', component: AccountSettings }] },
  {
    value: 'email',
    label: 'Email',
    icon: Mail,
    items: [
      { value: 'mailbox', label: 'Mailbox connection', component: MailboxConnectionSettings },
      { value: 'signature', label: 'Signature', component: EmailSignatureSettings },
      { value: 'voice', label: 'AI email voice', component: EmailVoiceSettings },
      { value: 'templates', label: 'Templates', component: EmailTemplatesSettings },
      { value: 'digest', label: 'Weekly digest', component: WeeklyDigestSettings },
    ],
  },
  {
    value: 'products',
    label: 'Products',
    icon: Package,
    items: [
      { value: 'skus', label: 'Available SKUs', component: AvailableSkusSettings },
      { value: 'new-product', label: 'New product defaults', component: ProductDefaultsSettings },
      { value: 'options', label: 'Categories, tags & tiers', component: ProductOptionsSettings },
      { value: 'tier-minimums', label: 'Tier minimums', component: WholesaleTierMinimumsSettings },
      { value: 'tier-review', label: 'Tier review', component: TierReviewSettingsCard },
      { value: 'units', label: 'Measurement units', component: UnitsSettings },
      { value: 'fixed-costs', label: 'Fixed costs', component: FixedCostsSettings },
    ],
  },
  {
    value: 'ai',
    label: 'AI tools',
    icon: Sparkles,
    items: [
      { value: 'descriptions', label: 'AI descriptions', component: DescriptionAiSettings },
      { value: 'photo-cleanup', label: 'Photo cleanup', component: PhotoCleanupSettings },
    ],
  },
  {
    value: 'channels',
    label: 'Sales channels',
    icon: Store,
    items: [{ value: 'faire', label: 'Faire', component: FaireSettings }],
  },
  {
    value: 'bags',
    label: 'Bags',
    icon: ShoppingBag,
    items: [
      { value: 'stock', label: 'Bag stock', component: BagsPanel },
      { value: 'reorder', label: 'Reorder', component: BagReorderSettings },
      { value: 'dimensions', label: 'Dimensions', component: BagDimensionsSettings },
    ],
  },
  {
    value: 'printing',
    label: 'Printing & Labels',
    icon: Printer,
    items: [{ value: 'picklist', label: 'Picklist PDF size', component: PrintLabelSettings }],
  },
  {
    value: 'shipping',
    label: 'Shipping',
    icon: Truck,
    items: [
      { value: 'shipping', label: 'Shipping', component: ShippingSettings },
      { value: 'tracking-email', label: 'Tracking email', component: TrackingEmailSettings },
    ],
  },
  {
    value: 'jobs',
    label: 'Scheduled jobs',
    icon: CalendarClock,
    items: [{ value: 'jobs', label: 'Scheduled jobs', component: ScheduledJobsSettings }],
  },
  {
    value: 'deleted',
    label: 'Recently deleted',
    icon: Trash2,
    items: [{ value: 'deleted', label: 'Recently deleted', component: RecentlyDeletedSettings }],
  },
]

// Old links pointed at a section whose cards have since moved.
const MOVED_ITEMS: Record<string, string> = {
  faire: 'channels',
  descriptions: 'ai',
  'photo-cleanup': 'ai',
}

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const itemParam = searchParams.get('item')
  const sectionParam =
    (itemParam && MOVED_ITEMS[itemParam]) || searchParams.get('section')

  const section = SECTIONS.find(s => s.value === sectionParam) ?? SECTIONS[0]!
  const item = section.items.find(i => i.value === itemParam) ?? section.items[0]!
  const Content = item.component

  const open = (sectionValue: string, itemValue?: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('section', sectionValue)
    if (itemValue) next.set('item', itemValue)
    else next.delete('item')
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Configure defaults for your pick &amp; pack workflow.
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        {/* Phone: one dropdown with every setting, grouped by section */}
        <label className="md:hidden">
          <span className="sr-only">Go to setting</span>
          <select
            value={`${section.value}/${item.value}`}
            onChange={e => {
              const [s = SECTIONS[0]!.value, i] = e.target.value.split('/')
              open(s, i)
            }}
            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {SECTIONS.map(s =>
              s.items.length === 1 ? (
                <option key={s.value} value={`${s.value}/${s.items[0]!.value}`}>
                  {s.label}
                </option>
              ) : (
                <optgroup key={s.value} label={s.label}>
                  {s.items.map(i => (
                    <option key={i.value} value={`${s.value}/${i.value}`}>
                      {s.label}: {i.label}
                    </option>
                  ))}
                </optgroup>
              )
            )}
          </select>
        </label>

        {/* Desktop: sections down the side; the open one lists its cards */}
        <nav className="hidden w-52 shrink-0 md:block" aria-label="Settings sections">
          <ul className="space-y-1">
            {SECTIONS.map(s => {
              const Icon = s.icon
              const active = s.value === section.value
              return (
                <li key={s.value}>
                  <button
                    type="button"
                    onClick={() => open(s.value)}
                    aria-current={active && s.items.length === 1 ? 'page' : undefined}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                      active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {s.label}
                  </button>
                  {active && s.items.length > 1 && (
                    <ul className="mt-1 mb-2 ml-5 space-y-0.5 border-l pl-2">
                      {s.items.map(i => {
                        const current = i.value === item.value
                        return (
                          <li key={i.value}>
                            <button
                              type="button"
                              onClick={() => open(s.value, i.value)}
                              aria-current={current ? 'page' : undefined}
                              className={cn(
                                'w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                                current
                                  ? 'font-medium text-foreground'
                                  : 'text-muted-foreground'
                              )}
                            >
                              {i.label}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>

        {/* One settings card at a time */}
        <div className="min-w-0 flex-1 space-y-6">
          <Content key={`${section.value}/${item.value}`} />
        </div>
      </div>
    </div>
  )
}
